/**
 * Job Order PDF generator — 3 styles.
 *
 *  classic  → formal serif letterhead, single column, conservative.
 *  modern   → orange SGK accent, modern sans-serif, photo grid.
 *  compact  → 1-2 page summary for quick contractor sign-off.
 *
 * Sections (all styles): Header, Job Details, Work Types, Safety, Method
 * of Statement, Risk Assessment matrix (color-coded by risk_score), Before
 * photos, After photos, Completion Checklist, Signatures.
 * Footer: SGK Apps™ — page X / N.
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function s(text) {
  if (text == null) return '';
  return String(text)
    .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
    .replace(/[\u201C\u201D\u201E]/g, '"')
    .replace(/[\u2014\u2013\u2010\u2011\u2012]/g, '-')
    .replace(/\u2026/g, '...').replace(/[\u2192\u27A4]/g, '->')
    .replace(/\u2022/g, '*').replace(/\u2122/g, '(TM)')
    .replace(/[\u00B7\u2027]/g, '-')
    .replace(/[^\x00-\x7E\u00A0-\u00FF]/g, '?');
}

const SGK_LOGO_PATH = '/branding/sgk-apps-logo.jpg';

async function loadLogo() {
  try {
    const r = await fetch(SGK_LOGO_PATH);
    const b = await r.blob();
    return await new Promise((res) => { const fr = new FileReader(); fr.onloadend = () => res(fr.result); fr.readAsDataURL(b); });
  } catch { return null; }
}

async function loadCompanyLogo(url) {
  if (!url) return null;
  try {
    const r = await fetch(url);
    const b = await r.blob();
    return await new Promise((res) => { const fr = new FileReader(); fr.onloadend = () => res(fr.result); fr.readAsDataURL(b); });
  } catch { return null; }
}

// risk_score → cell color
function riskColor(score) {
  if (score >= 16) return [220, 38, 38];       // critical rose
  if (score >= 10) return [234, 88, 12];       // high orange
  if (score >= 5)  return [217, 119, 6];       // medium amber
  return [22, 163, 74];                         // low green
}

function riskLabel(score) {
  if (score >= 16) return 'CRITICAL';
  if (score >= 10) return 'HIGH';
  if (score >= 5)  return 'MEDIUM';
  return 'LOW';
}

// ─────────────────── COMMON ───────────────────
function drawFooter(doc, style) {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.5);
    doc.line(32, H - 38, W - 32, H - 38);
    doc.setFont(style === 'classic' ? 'times' : 'helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(110, 110, 110);
    doc.text(s('SGK Apps (TM) - Branding & a Product of VRV Technical & Technology Solutions FZ LLC'),
      W / 2, H - 24, { align: 'center' });
    doc.text(s(`Page ${i} of ${total}`), W - 32, H - 24, { align: 'right' });
  }
}

function paragraph(doc, text, x, y, maxWidth, opts = {}) {
  doc.setFont(opts.font || 'helvetica', opts.style || 'normal');
  doc.setFontSize(opts.size || 10);
  doc.setTextColor(...(opts.color || [40, 50, 70]));
  const lines = doc.splitTextToSize(s(text || '—'), maxWidth);
  lines.forEach(line => {
    if (y > doc.internal.pageSize.getHeight() - 60) {
      doc.addPage();
      if (opts.onNewPage) opts.onNewPage();
      y = 70;
    }
    doc.text(line, x, y);
    y += (opts.lineHeight || 13);
  });
  return y;
}

// ─────────────────── HEADERS PER STYLE ───────────────────
function classicHeader(doc, { sgkLogo, companyLogo, company, jo }) {
  const W = doc.internal.pageSize.getWidth();
  // Letterhead band
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, W, 90, 'F');
  if (companyLogo) { try { doc.addImage(companyLogo, 'JPEG', 32, 18, 60, 54); } catch (e) { /* ignore */ } }
  doc.setFont('times', 'bold'); doc.setTextColor(20, 30, 60); doc.setFontSize(18);
  doc.text(s(company?.name || 'Your Company'), W / 2, 36, { align: 'center' });
  doc.setFont('times', 'normal'); doc.setFontSize(9); doc.setTextColor(80, 90, 110);
  if (company?.address) doc.text(s(company.address), W / 2, 50, { align: 'center' });
  if (company?.contact_phone || company?.contact_email) {
    doc.text(s([company.contact_phone, company.contact_email].filter(Boolean).join(' • ')), W / 2, 62, { align: 'center' });
  }
  if (sgkLogo) { try { doc.addImage(sgkLogo, 'JPEG', W - 78, 18, 46, 46); } catch (e) { /* ignore */ } }
  doc.setDrawColor(20, 30, 60); doc.setLineWidth(1.2); doc.line(32, 86, W - 32, 86);
  // Title
  doc.setFont('times', 'bold'); doc.setTextColor(20, 30, 60); doc.setFontSize(20);
  doc.text(s('JOB ORDER'), W / 2, 116, { align: 'center' });
  doc.setFont('times', 'normal'); doc.setFontSize(10); doc.setTextColor(80, 90, 110);
  doc.text(s(`Ref: ${jo.jo_number || '—'}    |    Date: ${(jo.created_at || '').slice(0, 10)}`), W / 2, 132, { align: 'center' });
}

function modernHeader(doc, { sgkLogo, companyLogo, company, jo }) {
  const W = doc.internal.pageSize.getWidth();
  // Orange band
  doc.setFillColor(240, 110, 35);
  doc.rect(0, 0, W, 90, 'F');
  if (companyLogo) { try { doc.addImage(companyLogo, 'JPEG', 32, 18, 54, 54); } catch (e) { /* ignore */ } }
  doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255); doc.setFontSize(22);
  doc.text(s('JOB ORDER'), W / 2, 42, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(11);
  doc.setTextColor(255, 235, 220);
  doc.text(s(jo.jo_number || ''), W / 2, 60, { align: 'center' });
  if (sgkLogo) { try { doc.addImage(sgkLogo, 'JPEG', W - 80, 18, 48, 48); } catch (e) { /* ignore */ } }
  // Sub band
  doc.setFillColor(255, 248, 240);
  doc.rect(0, 90, W, 28, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(180, 70, 20);
  doc.text(s(company?.name || 'Your Company'), 32, 108);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(120, 70, 40);
  doc.text(s(`Issued ${(jo.created_at || '').slice(0, 10)}`), W - 32, 108, { align: 'right' });
}

function compactHeader(doc, { sgkLogo, companyLogo, company, jo }) {
  const W = doc.internal.pageSize.getWidth();
  // Two-line slim header
  doc.setFillColor(240, 110, 35);
  doc.rect(0, 0, W, 4, 'F');
  if (companyLogo) { try { doc.addImage(companyLogo, 'JPEG', 32, 16, 32, 32); } catch (e) { /* ignore */ } }
  doc.setFont('helvetica', 'bold'); doc.setTextColor(15, 23, 42); doc.setFontSize(14);
  doc.text(s(company?.name || 'Your Company'), 70, 28);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(100, 116, 139);
  doc.text(s(`JOB ORDER  ·  ${jo.jo_number || ''}  ·  ${(jo.created_at || '').slice(0, 10)}`), 70, 42);
  if (sgkLogo) { try { doc.addImage(sgkLogo, 'JPEG', W - 60, 16, 28, 28); } catch (e) { /* ignore */ } }
  doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.5); doc.line(32, 56, W - 32, 56);
}

function sectionTitle(doc, text, y, style) {
  if (style === 'modern') {
    doc.setFillColor(240, 110, 35);
    doc.rect(32, y - 12, 4, 16, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(180, 70, 20);
    doc.text(s(text), 42, y);
  } else if (style === 'compact') {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(15, 23, 42);
    doc.text(s(text.toUpperCase()), 32, y);
    doc.setDrawColor(240, 110, 35); doc.setLineWidth(1); doc.line(32, y + 3, 32 + 80, y + 3);
  } else {
    doc.setFont('times', 'bold'); doc.setFontSize(12); doc.setTextColor(20, 30, 60);
    doc.text(s(text), 32, y);
    doc.setDrawColor(20, 30, 60); doc.setLineWidth(0.5); doc.line(32, y + 3, 200, y + 3);
  }
  return y + 18;
}

async function addPhotoGrid(doc, photos, y, style) {
  if (!photos?.length) {
    doc.setFont('helvetica', 'italic'); doc.setFontSize(9); doc.setTextColor(140, 150, 170);
    doc.text(s('(no photos attached)'), 32, y);
    return y + 16;
  }
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const cols = style === 'compact' ? 4 : 3;
  const gap = 8;
  const slot = (W - 64 - gap * (cols - 1)) / cols;
  let x = 32;
  let count = 0;
  for (const ph of photos.slice(0, style === 'compact' ? 4 : 9)) {
    if (y + slot > H - 60) {
      doc.addPage();
      y = 70;
      x = 32;
    }
    try {
      const fmt = ph.startsWith('data:image/png') ? 'PNG' : 'JPEG';
      doc.addImage(ph, fmt, x, y, slot, slot * 0.75);
    } catch {
      doc.setDrawColor(220, 220, 220); doc.rect(x, y, slot, slot * 0.75);
    }
    x += slot + gap;
    count += 1;
    if (count % cols === 0) {
      x = 32;
      y += slot * 0.75 + gap;
    }
  }
  if (count % cols !== 0) y += slot * 0.75 + gap;
  return y;
}

function renderRiskMatrix(doc, rows, y) {
  if (!rows?.length) {
    doc.setFont('helvetica', 'italic'); doc.setFontSize(9); doc.setTextColor(140, 150, 170);
    doc.text(s('(no risk assessment provided)'), 32, y);
    return y + 16;
  }
  autoTable(doc, {
    startY: y,
    head: [['#', 'Hazard', 'L', 'S', 'Risk', 'Controls', 'L', 'S', 'Residual']],
    body: rows.map((r, i) => [
      String(i + 1),
      s(r.hazard || ''),
      String(r.likelihood || 1),
      String(r.severity || 1),
      `${r.risk_score || (r.likelihood || 1) * (r.severity || 1)} (${riskLabel(r.risk_score || (r.likelihood || 1) * (r.severity || 1))})`,
      s(r.control_measures || ''),
      String(r.residual_likelihood || 1),
      String(r.residual_severity || 1),
      `${r.residual_score || (r.residual_likelihood || 1) * (r.residual_severity || 1)} (${riskLabel(r.residual_score || (r.residual_likelihood || 1) * (r.residual_severity || 1))})`,
    ]),
    styles: { fontSize: 8, cellPadding: 4, valign: 'middle' },
    headStyles: { fillColor: [240, 110, 35], textColor: 255, fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 18, halign: 'center' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 16, halign: 'center' },
      3: { cellWidth: 16, halign: 'center' },
      4: { cellWidth: 60, halign: 'center', fontStyle: 'bold' },
      5: { cellWidth: 'auto' },
      6: { cellWidth: 16, halign: 'center' },
      7: { cellWidth: 16, halign: 'center' },
      8: { cellWidth: 60, halign: 'center', fontStyle: 'bold' },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && (data.column.index === 4 || data.column.index === 8)) {
        const row = rows[data.row.index];
        const score = data.column.index === 4
          ? (row.risk_score || (row.likelihood || 1) * (row.severity || 1))
          : (row.residual_score || (row.residual_likelihood || 1) * (row.residual_severity || 1));
        data.cell.styles.fillColor = riskColor(score);
        data.cell.styles.textColor = 255;
      }
    },
    margin: { left: 32, right: 32 },
  });
  return doc.lastAutoTable.finalY + 14;
}

// ─────────────────── MAIN ───────────────────
export async function generateJobOrderPDF({ jo, company = {}, style = 'modern', view = false } = {}) {
  if (!jo) throw new Error('Job Order is required');
  const sgkLogo = await loadLogo();
  const companyLogo = await loadCompanyLogo(company?.logo_url);
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const headerCtx = { sgkLogo, companyLogo, company, jo };

  const drawHeader = () => {
    if (style === 'classic') classicHeader(doc, headerCtx);
    else if (style === 'compact') compactHeader(doc, headerCtx);
    else modernHeader(doc, headerCtx);
  };
  drawHeader();
  let y = style === 'classic' ? 156 : style === 'compact' ? 76 : 136;

  // 1. Job Details
  y = sectionTitle(doc, 'Job Details', y, style);
  const details = [
    ['Title', jo.title || '—'],
    ['Building', jo.building_name || '—'],
    ['Scope', jo.scope || '—'],
    ['Issued by', jo.created_by_name || '—'],
    ['Assigned to', `${jo.assigned_to_name || '—'}${jo.assignee_type === 'subcontractor' && jo.assigned_to_company ? ` (${jo.assigned_to_company})` : ''}`],
    ['Contact phone', jo.assigned_to_phone || '—'],
    ['Linked LPO', jo.related_lpo || '—'],
    ['Linked Work Order', jo.related_wo || '—'],
    ['Scheduled', `${jo.scheduled_start || '—'}${jo.scheduled_end ? ' → ' + jo.scheduled_end : ''}`],
    ['Status', (jo.status || 'draft').toUpperCase()],
  ];
  autoTable(doc, {
    startY: y,
    body: details.map(([k, v]) => [s(k), s(v)]),
    styles: { fontSize: 9, cellPadding: 4 },
    columnStyles: { 0: { cellWidth: 110, fontStyle: 'bold', fillColor: [248, 250, 252] } },
    theme: 'plain',
    margin: { left: 32, right: 32 },
  });
  y = doc.lastAutoTable.finalY + 14;

  // 2. Work Types
  if ((jo.work_types || []).length) {
    y = sectionTitle(doc, 'Work Types', y, style);
    paragraph(doc, jo.work_types.map(w => w.replace(/_/g, ' ')).join(', ').toUpperCase(), 32, y, W - 64, { onNewPage: drawHeader });
    y += 12;
  }

  // 3. Safety Instructions
  y = sectionTitle(doc, 'Safety Instructions', y, style);
  y = paragraph(doc, jo.safety_instructions, 32, y, W - 64, { onNewPage: drawHeader });

  // 4. Method of Statement
  y = sectionTitle(doc, 'Method of Statement', y, style);
  y = paragraph(doc, jo.method_of_statement, 32, y, W - 64, { onNewPage: drawHeader });

  // 5. Risk Assessment Matrix
  y = sectionTitle(doc, 'Risk Assessment (5×5 Matrix)', y, style);
  if (y > doc.internal.pageSize.getHeight() - 200) { doc.addPage(); drawHeader(); y = 70; }
  y = renderRiskMatrix(doc, jo.risk_assessment, y);

  // 6. Completion Checklist
  if ((jo.completion_checklist || []).length) {
    if (y > doc.internal.pageSize.getHeight() - 120) { doc.addPage(); drawHeader(); y = 70; }
    y = sectionTitle(doc, 'Completion Checklist', y, style);
    autoTable(doc, {
      startY: y,
      head: [['#', 'Task', 'Done', 'Notes']],
      body: (jo.completion_checklist || []).map((c, i) => [String(i + 1), s(c.name || ''), c.done ? '[x]' : '[ ]', s(c.notes || '')]),
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [30, 41, 59], textColor: 255 },
      columnStyles: { 0: { cellWidth: 22, halign: 'center' }, 2: { cellWidth: 36, halign: 'center' } },
      margin: { left: 32, right: 32 },
    });
    y = doc.lastAutoTable.finalY + 14;
  }

  // 7. Before Photos
  if (y > doc.internal.pageSize.getHeight() - 200) { doc.addPage(); drawHeader(); y = 70; }
  y = sectionTitle(doc, 'Before Photos', y, style);
  y = await addPhotoGrid(doc, jo.before_photos, y, style);

  // 8. After Photos
  if (y > doc.internal.pageSize.getHeight() - 200) { doc.addPage(); drawHeader(); y = 70; }
  y = sectionTitle(doc, 'After Photos', y, style);
  y = await addPhotoGrid(doc, jo.after_photos, y, style);

  // 9. Sign-off
  if (y > doc.internal.pageSize.getHeight() - 140) { doc.addPage(); drawHeader(); y = 70; }
  y = sectionTitle(doc, 'Sign-off', y, style);
  autoTable(doc, {
    startY: y,
    head: [['Role', 'Name', 'Date', 'Signature']],
    body: [
      ['Issued by', s(jo.created_by_name || '—'), s((jo.created_at || '').slice(0, 10)), ''],
      ['Completed by', s(jo.completed_by_name || '—'), s((jo.completed_at || '').slice(0, 10) || ''), ''],
      ['Reviewed by', s(jo.reviewer_name || '—'), s((jo.reviewed_at || '').slice(0, 10) || ''), ''],
    ],
    styles: { fontSize: 9, cellPadding: 8, minCellHeight: 36 },
    headStyles: { fillColor: [240, 110, 35], textColor: 255 },
    margin: { left: 32, right: 32 },
  });

  // Re-draw header on every page (we drew on page 1; others auto-redraw via onNewPage)
  drawFooter(doc, style);

  const filename = `JobOrder_${jo.jo_number || jo.id}_${style}.pdf`;
  if (view) window.open(URL.createObjectURL(doc.output('blob')), '_blank');
  else doc.save(filename);
}
